import React, { useState } from 'react';
import { AppointmentType as DBAppointmentType } from '@prisma/client';
import { differenceInMinutes } from 'date-fns';
import AppointmentType from './AppointmentType';
import TimeBlockForm from '../availability/TimeBlockForm';
import AppointmentForm, { CreateUpdateArgs } from './AppointmentForm';
import useCalendarSelectedAppointment from '../../hooks/appointments/useCalendarSelectedAppointment';
import useInitialAppointmentForm from '../../hooks/appointments/useInitialAppointmentForm';
import { CalendarSlotSelection } from '../../types/calendar';
import { MAX_APPOINTMENT_LENGTH_IN_MINUTES } from '../../constants/appointments';

interface Props {
  onClose: () => void;
  onCreateUpdateSuccess?: (args: CreateUpdateArgs) => void | Promise<void>;
  setShouldScrollToTop?: React.Dispatch<React.SetStateAction<boolean>>;
  slotSelection?: CalendarSlotSelection | null;
}

function CalendarNewForm({
  onClose,
  onCreateUpdateSuccess,
  setShouldScrollToTop,
  slotSelection,
}: Props) {
  const { selectedAppointment } = useCalendarSelectedAppointment();
  const { initialAppointmentForm, set: setInitialAppointmentForm } =
    useInitialAppointmentForm();

  const [appointmentType, setAppointmentType] = useState<
    DBAppointmentType | 'TIME_BLOCK' | 'BOOKING_LINK' | 'TOUCH_UP' | undefined
  >(initialAppointmentForm ? 'NEW_APPOINTMENT' : undefined);

  const getDraggedLength = () => {
    if (!slotSelection?.end) return undefined;
    const minutes = differenceInMinutes(slotSelection.end, slotSelection.start);
    return minutes > 0
      ? String(Math.min(minutes, MAX_APPOINTMENT_LENGTH_IN_MINUTES))
      : undefined;
  };

  const handleSetAppointmentType: typeof setAppointmentType = value => {
    if (slotSelection && typeof value === 'string' && value !== 'TIME_BLOCK') {
      const draggedLength = getDraggedLength();
      setInitialAppointmentForm({
        ...(slotSelection.employeeId && {
          employeeId: slotSelection.employeeId,
        }),
        ...(draggedLength && { appointmentLengthInMinutes: draggedLength }),
        startDateTime: slotSelection.start.toISOString(),
        isFromExistingAppointment: false,
      });
    }
    setAppointmentType(value);
  };

  return (
    <>
      {!appointmentType && !selectedAppointment && (
        <AppointmentType
          value={appointmentType}
          setFieldValue={handleSetAppointmentType}
          hiddenOptions={
            slotSelection ? ['WALK_IN', 'BOOKING_LINK'] : undefined
          }
        />
      )}
      {appointmentType === 'TIME_BLOCK' && (
        <TimeBlockForm onClose={onClose} slotSelection={slotSelection} />
      )}
      {appointmentType !== 'TIME_BLOCK' &&
        (appointmentType || selectedAppointment) && (
          <AppointmentForm
            onClose={onClose}
            onCreateUpdateSuccess={onCreateUpdateSuccess}
            setShouldScrollToTop={setShouldScrollToTop}
            appointmentType={
              appointmentType === 'BOOKING_LINK' ||
              appointmentType === 'TOUCH_UP'
                ? 'NEW_APPOINTMENT'
                : appointmentType
            }
            isBookingLink={appointmentType === 'BOOKING_LINK'}
            isTouchUp={appointmentType === 'TOUCH_UP'}
          />
        )}
    </>
  );
}

export default CalendarNewForm;
